/**
 * useTablesHandlers — Vague 3 refactor CaissePage (Feb 2026)
 *
 * Extrait les 5 handlers Tables (~160 lignes) hors du fichier principal.
 * Bloc auto-contenu : aucune dépendance à des helpers locaux non-exportables.
 *
 *   • fetchOpenTables(skipAutoSelect?)    — recharge la liste des tables ouvertes
 *   • createNewTable(tableNumber)         — ouverture d'une nouvelle table
 *   • selectTable(table | null)           — sélection d'une table active (ou reset)
 *   • saveCurrentTableToDb()              — persist current bill/notes on active table
 *   • closeTable(tableId, {force?})       — fermeture d'une table (avec fallback admin)
 *
 * Retourne aussi la référence `activeTable` (dérivée), et le useEffect
 * d'autosave debounced est INSTANCIÉ à l'intérieur du hook.
 */
import { useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { trySync } from "../../../lib/offlineSync";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export function useTablesHandlers(ctx) {
  const {
    // State (readable)
    currentUser, openTables, activeTableId,
    currentBill, selectedClient, paymentMethod, discount, notes, clients,
    // Setters
    setOpenTables, setAvailableTableNumbers, setActiveTableId,
    setCurrentBill, setSelectedClient, setPaymentMethod, setDiscount, setNotes,
    setShowNewTableModal,
    // Utils
    actorQs,
  } = ctx;

  // ────────────────────────────────────────────────
  // Sélection / reset (utilisée par les autres handlers)
  // ────────────────────────────────────────────────
  const selectTable = useCallback((table) => {
    if (!table) {
      setActiveTableId(null);
      setCurrentBill([]);
      setSelectedClient(null);
      setPaymentMethod("cash");
      setDiscount(0);
      setNotes("");
      return;
    }
    setActiveTableId(table.id);
    setCurrentBill(table.items || []);
    setSelectedClient(table.client_id ? clients.find((c) => c.id === table.client_id) : null);
    setPaymentMethod(table.payment_method || "cash");
    setDiscount(table.discount || 0);
    setNotes(table.notes || "");
  }, [clients, setActiveTableId, setCurrentBill, setSelectedClient, setPaymentMethod, setDiscount, setNotes]);

  // ────────────────────────────────────────────────
  // Fetch des tables ouvertes (+ auto-select conditionnelle)
  // ────────────────────────────────────────────────
  const fetchOpenTables = useCallback(async (skipAutoSelect = false) => {
    if (!currentUser) return;
    try {
      const serverId = currentUser.id || currentUser.username;
      const [tablesRes, availableRes] = await Promise.all([
        axios.get(`${API}/caisse/tables`, { params: { server_id: serverId } }),
        axios.get(`${API}/caisse/tables/available`, { params: { server_id: serverId } }),
      ]);
      const tables = tablesRes.data.tables || [];
      setOpenTables(tables);
      setAvailableTableNumbers(availableRes.data.available_tables || []);
      // Auto-select uniquement si:
      // - aucune table active, tables disponibles, skipAutoSelect=false
      // - la table doit être non-facturée et avoir des items
      if (!activeTableId && tables.length > 0 && !skipAutoSelect) {
        const auto = tables.find((t) => t.status !== "invoiced" && (t.items?.length > 0));
        if (auto) selectTable(auto);
      }
    } catch (error) {
      console.error("Error fetching tables:", error);
    }
  }, [currentUser, activeTableId, setOpenTables, setAvailableTableNumbers, selectTable]);

  // ────────────────────────────────────────────────
  // Création d'une nouvelle table (avec fallback offline)
  // ────────────────────────────────────────────────
  const createNewTable = useCallback(async (tableNumber) => {
    if (!currentUser) return;
    // Clear local immédiat — évite qu'un auto-save 500ms écrive les items
    // de la table précédente sur la nouvelle.
    setActiveTableId(null);
    setCurrentBill([]);
    setSelectedClient(null);
    setPaymentMethod("cash");
    setDiscount(0);
    setNotes("");
    try {
      const payload = {
        id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `t_${Date.now()}`,
        table_number: tableNumber,
        server_id: currentUser.id || currentUser.username,
        server_name: currentUser.full_name || currentUser.username,
        items: [],
        client_name: "Client",
        payment_method: "cash",
        discount: 0,
        notes: "",
      };
      const r = await trySync({
        type: "create_table",
        payload,
        user: { name: currentUser.full_name || currentUser.username, role: currentUser.role },
      });
      if (r.queued) {
        toast.warning(`Table ${tableNumber} créée hors-ligne — sera synchronisée au retour de la connexion`);
        const localTable = { ...payload, _offline_pending: true, created_at: new Date().toISOString() };
        await fetchOpenTables();
        selectTable(localTable);
        setShowNewTableModal(false);
        return;
      }
      if (r.data?.success) {
        toast.success(`Table ${tableNumber} ouverte !`);
        await fetchOpenTables();
        selectTable(r.data.table);
        setShowNewTableModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ouverture de la table");
    }
  }, [currentUser, fetchOpenTables, selectTable,
      setActiveTableId, setCurrentBill, setSelectedClient, setPaymentMethod,
      setDiscount, setNotes, setShowNewTableModal]);

  // ────────────────────────────────────────────────
  // Persistance de la table courante (débouncée par le useEffect ci-dessous)
  // ────────────────────────────────────────────────
  const saveCurrentTableToDb = useCallback(async () => {
    if (!activeTableId) return;
    try {
      await axios.put(`${API}/caisse/tables/${activeTableId}?${actorQs()}`, {
        items: currentBill,
        client_id: selectedClient?.id || null,
        client_name: selectedClient?.name || "Client",
        payment_method: paymentMethod,
        discount: discount,
        notes: notes,
      });
    } catch (error) {
      console.error("Error saving table:", error);
    }
  }, [activeTableId, currentBill, selectedClient, paymentMethod, discount, notes, actorQs]);

  // ────────────────────────────────────────────────
  // Fermeture (soft ou forcée par admin si plats en cuisine)
  // ────────────────────────────────────────────────
  const closeTable = useCallback(async function _closeTable(tableId, options = {}) {
    const { force = false } = options;
    try {
      const forceParam = force ? "&force=true" : "";
      await axios.delete(`${API}/caisse/tables/${tableId}?${actorQs()}&reason=cancelled${forceParam}`);
      if (tableId === activeTableId) {
        const remainingTables = openTables.filter((t) => t.id !== tableId);
        if (remainingTables.length > 0) selectTable(remainingTables[0]);
        else selectTable(null);
      }
      await fetchOpenTables();
    } catch (error) {
      // 409 : plat(s) en cours de préparation en cuisine
      if (error?.response?.status === 409 && !force) {
        const detail = error.response.data?.detail || "Plats en préparation";
        if (currentUser?.role === "admin") {
          // eslint-disable-next-line no-alert
          if (window.confirm(`${detail}\n\nForcer la suppression quand même (admin) ?`)) {
            return _closeTable(tableId, { force: true });
          }
        } else {
          toast.error(detail);
        }
      } else {
        console.error("Error closing table:", error);
        toast.error(error?.response?.data?.detail || "Erreur fermeture table");
      }
    }
  }, [actorQs, activeTableId, openTables, currentUser, selectTable, fetchOpenTables]);

  // ────────────────────────────────────────────────
  // Auto-save débounce 500ms (déplacé depuis CaissePage)
  // ────────────────────────────────────────────────
  useEffect(() => {
    if (activeTableId && currentBill.length >= 0) {
      const timeoutId = setTimeout(() => {
        saveCurrentTableToDb();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [currentBill, selectedClient, paymentMethod, discount, notes, activeTableId, saveCurrentTableToDb]);

  // ────────────────────────────────────────────────
  // Dérivée : table active courante
  // ────────────────────────────────────────────────
  const activeTable = useMemo(
    () => openTables.find((t) => t.id === activeTableId),
    [openTables, activeTableId],
  );

  return {
    fetchOpenTables,
    createNewTable,
    selectTable,
    saveCurrentTableToDb,
    closeTable,
    activeTable,
  };
}
